<!-- Console delegation V1.0; 2026-10-07 09:05:47 CST; parent dcebd36069b2ddb3e45e586f2c90a0214b9a2a4f. -->
<!-- HotX Console 0.1.121 域名管理默认首页与首次改密会话保护。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<!-- Console session recovery 0.1.152; 2026-10-28 10:33:55 CST; parent 5cc4139ab88215e2a94aab6d2c979dd79f4cf085. -->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router';
import { buildConsoleReleaseLabel, consoleReleaseInfo, formatConsoleBuildTime } from './lib/release';
import { useAuthStore } from './stores/auth';
import { usePointStore } from './stores/points';
import { displayThreeDecimal } from './utils/formatters';
import GuestAuthDialog from './components/GuestAuthDialog.vue';
import DelegationBanner from './components/DelegationBanner.vue';
import { inDelegationContext } from './auth/delegationSession';
import { guestPageAccessForPath } from './auth/guestPageAccess';
import {
  GUEST_AUTH_REQUIRED_EVENT,
  cancelGuestAuthentication,
  completeGuestAuthentication,
  type GuestAuthRequestDetail
} from './auth/guestAuthGate';
import { CONSOLE_SESSION_CHANGED_EVENT, consoleApi, tokenStorage } from './api/client';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const points = usePointStore();
const headerLoading = ref(false)
const headerRefreshPending = ref(false)
const isGuest = ref(!tokenStorage.getAccessToken());
const guestAuthVisible = ref(false);
const guestAuthReason = ref('登录后即可继续当前操作');
const guestAuthRequestedByMutation = ref(false);
const mobileNavigationOpen = ref(false);
const mobileNavigationTrigger = ref<HTMLButtonElement | null>(null);
const mobileNavigationClose = ref<HTMLButtonElement | null>(null);
// 更新时间：2026-09-04 15:25 CST。账户资料只保留在当前登录会话内，进入游客态时立即清空，防止跨账号残留。
const accountProfile = ref<Record<string, unknown>>({});
// 更新时间：2026-09-04 12:12 CST。仅在进入游客态时重建业务页，清除内存中的账户数据；登录成功不变更该值，确保表单和待续操作不丢失。
const privateViewEpoch = ref(0);
const releaseLabel = buildConsoleReleaseLabel(consoleReleaseInfo);
const releaseTitle = `构建时间：${formatConsoleBuildTime(consoleReleaseInfo.buildTime, true)} CST`;
const websiteUrl = import.meta.env.VITE_WEBSITE_URL || 'https://hotxcdn.net';

const isLogin = computed(() => ['/login', '/delegation', '/delegation-ended'].includes(route.path));
const guestPageAccess = computed(() => guestPageAccessForPath(route.path));
const unreadNotificationCount = computed(() => points.unreadNotificationCount);
const hasUnreadNotification = computed(() => unreadNotificationCount.value > 0);
// V1.1 / 2026-09-10 CST：只以积分事实源的 todaySigned 判断完成态，避免本地点击造成虚假“已签到”。
const headerSigninComplete = computed(() => !isGuest.value && Boolean(points.summary?.todaySigned));
const headerSigninLabel = computed(() => headerSigninComplete.value ? '已签到' : '签到领积分');
const headerAccountLabel = computed(() => {
  const username = firstAccountIdentity(
    accountProfile.value.username,
    accountProfile.value.customerName,
    accountProfile.value.displayName
  );
  const email = firstAccountIdentity(accountProfile.value.email, accountProfile.value.loginEmail);
  return username || email || 'HotX 用户';
});
const headerAccountTitle = computed(() => {
  const email = firstAccountIdentity(accountProfile.value.email, accountProfile.value.loginEmail);
  return email && email !== headerAccountLabel.value
    ? `${headerAccountLabel.value} · ${email}`
    : headerAccountLabel.value;
});
const headerAccountAvatar = computed(() => headerAccountLabel.value.trim().charAt(0).toLocaleUpperCase() || 'H');

async function refreshHeaderData() {
  if (isGuest.value) return;
  if (headerLoading.value) {
    headerRefreshPending.value = true;
    return;
  }
  headerLoading.value = true;
  try {
    if (auth.mustChangePassword) {
      points.clear();
      accountProfile.value = await consoleApi.account.profile() as unknown as Record<string, unknown>;
      return;
    }
    const accountRequest = consoleApi.portal.account()
      .then(payload => {
        accountProfile.value = (payload as { profile?: Record<string, unknown> } | null)?.profile ?? {};
      })
      .catch(() => {
        accountProfile.value = {};
      });
    await Promise.all([
      points.refresh(),
      points.refreshNotifications(),
      accountRequest
    ]);
  } finally {
    headerLoading.value = false;
    const shouldRefreshAgain = headerRefreshPending.value && !isGuest.value;
    headerRefreshPending.value = false;
    if (shouldRefreshAgain) {
      refreshHeaderData().catch(() => undefined);
    }
  }
}

watch([isLogin, isGuest], ([login, guest]) => {
  if (login || guest) {
    points.clear();
    return;
  }
  refreshHeaderData().catch(() => undefined);
}, { immediate: true });
watch(() => route.fullPath, () => closeMobileNavigation());
watch(mobileNavigationOpen, open => {
  document.documentElement.classList.toggle('console-mobile-nav-open', open);
});
onMounted(() => {
  // 2026-09-03 CST：充值页入账后只刷新共享积分 Store，不重载整页，保留用户当前操作和滚动位置。
  window.addEventListener('hotx:points-updated', refreshPointsAfterRecharge);
  // 2026-09-04 11:34 CST：统一接管游客写操作和登录状态变化，避免业务页面跳转或丢失输入。
  window.addEventListener(GUEST_AUTH_REQUIRED_EVENT, handleGuestAuthRequired);
  window.addEventListener(CONSOLE_SESSION_CHANGED_EVENT, syncConsoleSession);
  window.addEventListener('keydown', handleMobileNavigationKeydown);
});
function refreshPointsAfterRecharge() {
  points.refresh().catch(() => undefined);
}
onBeforeUnmount(() => {
  window.removeEventListener('hotx:points-updated', refreshPointsAfterRecharge);
  window.removeEventListener(GUEST_AUTH_REQUIRED_EVENT, handleGuestAuthRequired);
  window.removeEventListener(CONSOLE_SESSION_CHANGED_EVENT, syncConsoleSession);
  window.removeEventListener('keydown', handleMobileNavigationKeydown);
  document.documentElement.classList.remove('console-mobile-nav-open');
});
const breadcrumbs = computed(() => {
  const raw = route.meta.breadcrumb;
  const items = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
  return ['控制台首页', ...items.map(String)];
});

const menus = [
  { path: '/domains', label: '域名管理', icon: 'DN' },
  { path: '/dashboard', label: '概览', icon: 'OV' },
  { path: '/packages', label: '套餐管理', icon: 'PK' },
  { path: '/points', label: '积分中心', icon: 'PT' },
  // 2026-09-03 CST：TRX 充值已拆分为独立页面；菜单顺序固定在积分中心与 BSC 充值之间。
  { path: '/trx-recharge', label: 'TRX 充值', icon: 'TX' },
  { path: '/bsc-recharge', label: 'BSC 充值', icon: 'BS' },
  { path: '/traffic', label: '流量监控', icon: 'TR' },
  { path: '/cache', label: '刷新缓存', icon: 'RF' },
  { path: '/security', label: '安全防护', icon: 'SC' },
  { path: '/ip-collections', label: 'IP 集合', icon: 'IP' },
  { path: '/policies', label: '策略管理', icon: 'PL' },
  { path: '/https', label: '证书管理', icon: 'CT' },
  { path: '/origins', label: '源站管理', icon: 'OR' },
  { path: '/logs', label: '日志服务', icon: 'LG' },
  { path: '/account', label: '账户中心', icon: 'ME' }
];

async function openMobileNavigation() {
  mobileNavigationOpen.value = true;
  await nextTick();
  mobileNavigationClose.value?.focus();
}

async function closeMobileNavigation(restoreFocus = false) {
  if (!mobileNavigationOpen.value) return;
  mobileNavigationOpen.value = false;
  if (restoreFocus) {
    await nextTick();
    mobileNavigationTrigger.value?.focus();
  }
}

function handleMobileNavigationKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && mobileNavigationOpen.value) {
    closeMobileNavigation(true);
  }
}

async function logout() {
  const delegated = inDelegationContext();
  await auth.logout();
  if (delegated) { window.location.replace('/delegation-ended'); return; }
  enterGuestMode();
}

function enterGuestMode() {
  const wasGuest = isGuest.value;
  isGuest.value = true;
  points.clear();
  accountProfile.value = {};
  if (!wasGuest) {
    privateViewEpoch.value += 1;
  }
}

function handleGuestAuthRequired(event: Event) {
  const detail = (event as CustomEvent<GuestAuthRequestDetail>).detail;
  guestAuthRequestedByMutation.value = true;
  guestAuthReason.value = detail?.reason || '登录后即可继续当前操作';
  guestAuthVisible.value = true;
}

function openGuestAuthDialog() {
  guestAuthRequestedByMutation.value = false;
  guestAuthReason.value = guestPageAccess.value.authReason;
  guestAuthVisible.value = true;
}

async function handleGuestAuthenticated() {
  // Google 登录发生在弹窗 window，父窗口不会收到弹窗内派发的 session event；必须先从共享存储同步。
  auth.syncFromStorage();
  const reloadPrivatePage = !guestAuthRequestedByMutation.value && guestPageAccess.value.reloadAfterLogin;
  isGuest.value = false;
  guestAuthVisible.value = false;
  if (auth.mustChangePassword) {
    // 首次改密令牌只允许账户资料与改密接口，禁止恢复刚才暂停的业务写请求。
    cancelGuestAuthentication('首次登录需先修改密码，原操作尚未提交。完成改密后请重新操作。');
    guestAuthRequestedByMutation.value = false;
    await router.replace('/account');
    refreshHeaderData().catch(() => undefined);
    return;
  }
  if (reloadPrivatePage) privateViewEpoch.value += 1;
  // The Google popup writes shared storage in another window. Notify preserved pages
  // after syncing the opener so their pre-login reads cannot leave a stale 401 behind.
  window.dispatchEvent(new Event(CONSOLE_SESSION_CHANGED_EVENT));
  completeGuestAuthentication();
  guestAuthRequestedByMutation.value = false;
  refreshHeaderData().catch(() => undefined);
}

function handleGuestAuthCancelled() {
  guestAuthVisible.value = false;
  cancelGuestAuthentication();
}

function syncConsoleSession() {
  auth.syncFromStorage();
  const nextIsGuest = !tokenStorage.getAccessToken();
  if (nextIsGuest) {
    // 更新时间：2026-09-04 12:12 CST。会话退出或失效时建立隐私边界，禁止上一位用户的数据残留在游客页面。
    enterGuestMode();
    return;
  }
  isGuest.value = false;
  refreshHeaderData().catch(() => undefined);
}

function firstAccountIdentity(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}
</script>

<template>
  <RouterView v-if="isLogin" />
  <el-container v-else class="console-shell" :class="{ 'console-shell--guest': isGuest }">
    <el-aside
      id="console-navigation"
      width="232px"
      class="sidebar"
      :class="{ 'sidebar--mobile-open': mobileNavigationOpen }"
      aria-label="控制台主导航"
    >
      <div class="sidebar-brand-row">
        <RouterLink to="/domains" class="logo" aria-label="返回控制台首页">
          <span class="brand-mark">X</span>
          <span class="brand-text">HotX CDN</span>
        </RouterLink>
        <button
          ref="mobileNavigationClose"
          class="mobile-sidebar-close"
          type="button"
          aria-label="关闭控制台导航"
          @click="closeMobileNavigation(true)"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <nav aria-label="控制台功能菜单">
        <RouterLink
          v-for="item in menus"
          :key="item.path"
          :to="item.path"
          class="menu-item"
          active-class="active"
        >
          <span class="menu-icon" aria-hidden="true">{{ item.icon }}</span>
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <nav class="mobile-sidebar-shortcuts" aria-label="控制台快捷入口">
        <a :href="websiteUrl" target="_blank" rel="noopener noreferrer" aria-label="移动端访问官网">官网</a>
        <RouterLink :to="headerSigninComplete ? '/points' : '/points?action=sign-in'">{{ headerSigninLabel }}</RouterLink>
        <RouterLink to="/notifications">消息</RouterLink>
        <a :href="`${websiteUrl}/solutions`" target="_blank" rel="noopener noreferrer" aria-label="移动端打开帮助中心">帮助</a>
      </nav>
      <div class="release-badge" :title="releaseTitle">{{ releaseLabel }}</div>
    </el-aside>
    <button
      v-if="mobileNavigationOpen"
      class="mobile-nav-backdrop"
      type="button"
      aria-label="关闭控制台导航"
      @click="closeMobileNavigation(true)"
    />
    <el-container class="console-content">
      <el-header class="top-header">
        <div class="top-header-brand">
          <button
            ref="mobileNavigationTrigger"
            class="mobile-nav-trigger"
            type="button"
            aria-label="打开控制台导航"
            aria-controls="console-navigation"
            :aria-expanded="mobileNavigationOpen"
            data-test="mobile-nav-trigger"
            @click="openMobileNavigation"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
          <span class="top-header-title">
            <span class="top-header-title-full">HotX CDN 客户控制台</span>
            <span class="top-header-title-short">客户控制台</span>
          </span>
        </div>
        <div class="header-actions">
          <nav class="header-nav" aria-label="客户控制台快捷入口">
            <a class="header-nav-link" :href="websiteUrl" target="_blank" rel="noopener noreferrer" aria-label="访问官网">
              访问官网
            </a>
            <RouterLink class="header-nav-link" to="/packages" aria-label="查看费用中心">费用中心</RouterLink>
            <RouterLink class="header-nav-link" to="/points" aria-label="查看积分余额">积分 {{ isGuest ? '--' : displayThreeDecimal(points.summary?.balance) }}</RouterLink>
            <RouterLink
              class="header-nav-link header-signin-link"
              :class="{
                'header-signin-link--active': !isGuest && !headerSigninComplete,
                'header-signin-link--settled': headerSigninComplete
              }"
              :to="headerSigninComplete ? '/points' : '/points?action=sign-in'"
              :aria-label="headerSigninLabel"
              :data-signin-state="headerSigninComplete ? 'signed' : 'pending'"
              data-test="header-signin-link"
            >
              <span v-if="!headerSigninComplete" class="header-signin-coin" aria-hidden="true">1</span>
              {{ headerSigninLabel }}
            </RouterLink>
            <RouterLink
              class="header-nav-link header-icon-link"
              to="/notifications"
              aria-label="查看消息中心"
            >
              <span
                class="notification-dot"
                :class="{ 'notification-dot--unread': !isGuest && hasUnreadNotification }"
                aria-hidden="true"
              />
              消息
              <span v-if="!isGuest && hasUnreadNotification" class="notification-dot-count">{{ unreadNotificationCount }}</span>
            </RouterLink>
            <a
              class="header-nav-link"
              :href="`${websiteUrl}/solutions`"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="打开帮助中心"
            >
              帮助中心
            </a>
          </nav>
          <button v-if="isGuest" class="guest-login-trigger" type="button" @click="openGuestAuthDialog">
            <span class="account-avatar">游</span>
            <span>登录 / 注册</span>
          </button>
          <el-dropdown v-else trigger="click" class="account-dropdown header-profile">
            <button class="account-trigger" type="button" aria-label="打开个人中心菜单">
              <span class="account-avatar">{{ headerAccountAvatar }}</span>
              <span class="account-identity" :title="headerAccountTitle">{{ headerAccountLabel }}</span>
              <span class="account-caret">⌄</span>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item>
                  <RouterLink to="/account">个人中心</RouterLink>
                </el-dropdown-item>
                <el-dropdown-item>
                  <RouterLink to="/account">账户安全</RouterLink>
                </el-dropdown-item>
                <el-dropdown-item>
                  <RouterLink to="/logs">操作记录</RouterLink>
                </el-dropdown-item>
                <el-dropdown-item>
                  <a :href="websiteUrl" target="_blank" rel="noopener noreferrer">访问官网</a>
                </el-dropdown-item>
                <el-dropdown-item divided @click="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main>
        <DelegationBanner />
        <div class="console-frame">
          <div v-if="isGuest" class="guest-preview-strip" role="status">
            <span class="guest-preview-dot" aria-hidden="true" />
            <strong>当前未登录</strong>
            <span>{{ guestPageAccess.message }}</span>
            <button type="button" @click="openGuestAuthDialog">现在登录</button>
          </div>
          <div
            v-else-if="auth.mustChangePassword && route.path !== '/account'"
            class="guest-preview-strip first-password-strip"
            role="alert"
          >
            <span class="guest-preview-dot" aria-hidden="true" />
            <strong>首次登录需先修改密码</strong>
            <span>原操作尚未提交；修改完成后请重新操作。</span>
            <RouterLink to="/account">前往账户中心</RouterLink>
          </div>
          <el-breadcrumb v-if="breadcrumbs.length > 1" class="breadcrumb" separator="/">
            <el-breadcrumb-item>
              <RouterLink to="/domains" aria-label="面包屑：返回控制台首页">控制台首页</RouterLink>
            </el-breadcrumb-item>
            <el-breadcrumb-item v-for="item in breadcrumbs.slice(1)" :key="item">
              {{ item }}
            </el-breadcrumb-item>
          </el-breadcrumb>
          <!-- 更新时间：2026-09-04 12:12 CST。只在进入游客态时重建当前业务页；登录成功不重建，原表单与提交 Promise 会继续。 -->
          <RouterView :key="privateViewEpoch" />
        </div>
      </el-main>
    </el-container>
  </el-container>
  <GuestAuthDialog
    v-if="!isLogin"
    v-model="guestAuthVisible"
    :reason="guestAuthReason"
    @authenticated="handleGuestAuthenticated"
    @cancel="handleGuestAuthCancelled"
  />
</template>

<style scoped>
.guest-preview-strip {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border: 1px solid #d6e8df;
  border-radius: 10px;
  color: #48665c;
  background: linear-gradient(100deg, #f2faf6, #fff9ec);
  box-shadow: 0 8px 24px rgba(32, 87, 65, 0.06);
  font-size: 13px;
}

.guest-preview-strip strong {
  color: #155b42;
}

.guest-preview-strip button,
.guest-preview-strip a {
  margin-left: auto;
  border: 0;
  color: #0f6447;
  background: transparent;
  cursor: pointer;
  font-weight: 900;
}

.guest-preview-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #e7a72b;
  box-shadow: 0 0 0 5px rgba(231, 167, 43, 0.14);
}

.guest-login-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  border: 1px solid #cfe1d9;
  border-radius: 999px;
  padding: 4px 14px 4px 5px;
  color: #154f3c;
  background: linear-gradient(180deg, #fff, #f4faf7);
  cursor: pointer;
  font-weight: 850;
  box-shadow: 0 7px 18px rgba(23, 80, 66, 0.09);
}

/* 更新时间：2026-09-04 11:34 CST。游客接口拒绝仅用于保护私有数据，不在参观页面展示红色系统错误。 */
.console-shell--guest :deep(.el-alert--error) {
  display: none;
}

.header-icon-link {
  position: relative;
}

.account-identity {
  display: block;
  overflow: hidden;
  max-width: 156px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-dot {
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 50%;
  background: rgba(23, 80, 66, 0.25);
  transition: background 0.2s ease, transform 0.2s ease;
}

.notification-dot--unread {
  background: #e74c3c;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.16);
  transform: scale(1.1);
}

.notification-dot-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  color: #fff;
  background: #e74c3c;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
}

@media (max-width: 760px) {
  .guest-preview-strip {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .guest-preview-strip button {
    width: 100%;
    min-height: 44px;
    margin-left: 0;
  }
}
</style>
